import Decimal from 'decimal.js';
import { applyFiBasisToDisposalDetails } from './fiDisposalBasis';
import type { DisposalDetail } from './types';

/**
 * One Crypto.com CSV row → one ledger disposal, but FIFO may emit several `DisposalDetail` lines
 * when that disposal consumes multiple acquisition lots (oldest first). That is correct for tax
 * but looks like duplicate rows in the UI. Merge slices that share the same CDC `ref` + timestamp + asset.
 */
export function mergeCryptoComFifoSlicesForDisplay(details: DisposalDetail[]): DisposalDetail[] {
  const groupKey = (d: DisposalDetail): string | null => {
    if (d.source !== 'crypto_com') return null;
    const r = d.ref?.trim();
    if (!r || !r.startsWith('crypto_com_row_')) return null;
    return `${r.toLowerCase()}\0${d.ts}\0${d.asset.toUpperCase()}`;
  };

  const byKey = new Map<string, DisposalDetail[]>();
  for (const d of details) {
    const k = groupKey(d);
    if (k == null) continue;
    const arr = byKey.get(k) ?? [];
    arr.push(d);
    byKey.set(k, arr);
  }

  const out: DisposalDetail[] = [];
  const emitted = new Set<string>();

  for (const d of details) {
    const k = groupKey(d);
    if (k == null) {
      out.push(d);
      continue;
    }
    if (emitted.has(k)) continue;
    emitted.add(k);
    const g = byKey.get(k)!;
    if (g.length === 1) {
      out.push(g[0]!);
      continue;
    }
    out.push(applyFiBasisToDisposalDetails([mergeFifoSlicesGroup(g)])[0]!);
  }
  return out;
}

function mergeFifoSlicesGroup(slices: DisposalDetail[]): DisposalDetail {
  const first = slices[0]!;
  let disposed = new Decimal(0);
  let proceedsEur = new Decimal(0);
  let costEur = new Decimal(0);
  let gainEur = new Decimal(0);
  let minLotAcquired: number | undefined;
  for (const s of slices) {
    disposed = disposed.plus(s.disposed);
    proceedsEur = proceedsEur.plus(s.proceedsEur);
    costEur = costEur.plus(s.costEur);
    gainEur = gainEur.plus(s.gainEur);
    if (s.lotAcquiredAt != null) {
      minLotAcquired =
        minLotAcquired == null
          ? s.lotAcquiredAt
          : Math.min(minLotAcquired, s.lotAcquiredAt);
    }
  }

  return {
    ...first,
    disposed,
    proceedsEur,
    costEur,
    gainEur,
    lotAcquiredAt: minLotAcquired,
    fifoMergedFromSlices: slices.length,
    fifoCostBasisEur: undefined,
    deemedCostBasisEur: undefined,
    chosenCostBasisEur: undefined,
    basisMethodUsed: undefined,
    fifoGainEur: undefined,
    deemedGainEur: undefined,
    chosenGainEur: undefined,
    holdingYearsAtDisposal: undefined,
    deemedHoldingBracket: undefined,
    deemedPreventedDueToZeroProceeds: undefined,
  };
}
